// src/features/residents/layout/ResidentLayout.tsx
import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Megaphone, BarChart3, AlertTriangle, CreditCard, User, LogOut, ChevronDown } from "lucide-react";
import { getResidentProfile } from "@/api/residentAuth";
import { setAccessToken } from "@/api/client";
import { logout as apiLogout } from "@/api/auth";
import { useLanguage } from "@/contexts/LanguageContext";
import { queryClient } from "@/lib/queryClient";

const PRIMARY = "#1F3A93"; // brand blue
const BG = "#F5F6FA";

export default function ResidentLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [residentName, setResidentName] = useState("Resident");
  const [unitCode, setUnitCode] = useState("");
  const [membershipsCount, setMembershipsCount] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only check auth if we're actually on a resident route
    if (!pathname.startsWith('/resident')) {
      return;
    }
    
    // Load resident info — always fetch fresh from API, don't trust localStorage name
    const loadResidentInfo = async () => {
      const residentToken = localStorage.getItem('resident_access_token');
      const sessionToken = sessionStorage.getItem('access_token');
      const sessionRole = sessionStorage.getItem('role');
      const accessToken = residentToken || (sessionRole === 'RESIDENT' ? sessionToken : null);
      
      console.log('[ResidentLayout] Checking auth:', { 
        hasAccessToken: !!accessToken,
        pathname 
      });
      
      if (accessToken) {
        // Keep resident token storage in sync when login came from HOA tab.
        if (!residentToken) {
          localStorage.setItem('resident_access_token', accessToken);
        }

        // Set token for API calls
        setAccessToken(accessToken);
        
        // Fetch fresh profile data
        try {
          const response = await getResidentProfile();
          console.log('[ResidentLayout] Profile loaded:', response);
          if (response.success) {
            setResidentName(response.data.full_name);
            // Keep localStorage in sync with the authoritative API value
            localStorage.setItem('resident_name', response.data.full_name);
            setUnitCode(response.data.unit?.unit_code || '');
            setMembershipsCount(response.data.memberships?.length || 1);

            // If setup not completed, redirect to setup page
            if (!response.data.has_completed_setup) {
              navigate('/resident/setup');
              return;
            }
          }
        } catch (err) {
          console.error('[ResidentLayout] Failed to load profile:', err);
          // Only logout on 401 errors, not other errors
          const axiosError = err as any;
          if (axiosError?.response?.status === 401) {
            handleLogout();
            return;
          }
          // For other errors, fall back to stored name only if we have no fresher data
          const storedName = localStorage.getItem('resident_name');
          if (storedName) setResidentName(storedName);
        }
      } else {
        // No token, redirect to login page (not OTP page)
        console.log('[ResidentLayout] No token found, redirecting to login');
        navigate('/login', { replace: true });
        return;
      }
      
      setIsLoading(false);
    };
    
    loadResidentInfo();
  }, [pathname, navigate]);

  async function handleLogout() {
    try {
      // Clear refresh cookie on server before local cleanup.
      await apiLogout();

      // Clear in-memory token (critical!)
      setAccessToken(null);
      
      // Clear ALL cached query data so the next user sees no stale data
      queryClient.clear();

      // Clear session-based auth state (needed for resident sessions bridged via sessionStorage)
      sessionStorage.clear();
      
      // Clear resident-specific localStorage items (NOT all localStorage)
      localStorage.removeItem('resident_access_token');
      localStorage.removeItem('resident_refresh_token');
      localStorage.removeItem('resident_id');
      localStorage.removeItem('resident_name');
      localStorage.removeItem('resident_email');
      
      // Redirect to login page
      window.location.href = "/#/login";
    } catch (error) {
      console.error("Logout failed:", error);
      
      // Force logout anyway
      setAccessToken(null);
      queryClient.clear();
      localStorage.removeItem('resident_access_token');
      localStorage.removeItem('resident_refresh_token');
      localStorage.removeItem('resident_id');
      localStorage.removeItem('resident_name');
      localStorage.removeItem('resident_email');
      
      // Force reload
      window.location.href = "/#/login";
    }
  }

  return (
    <div className="min-h-screen flex flex-col relative" style={{ backgroundColor: BG }}>
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1000px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Header (glass) */}
      <header className="sticky top-0 z-40">
        <div className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
          <div className="max-w-5xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <h1 className="text-[17px] font-semibold leading-tight tracking-[-0.01em]">
                  {titleFromPath(pathname, t)}
                </h1>
                <div className="flex items-center gap-2 text-[13px] text-gray-500">
                  <span>{residentName}</span>
                  <span>•</span>
                  <span>{t("roles.resident")}</span>
                  {unitCode && (
                    <>
                      <span>•</span>
                      {membershipsCount > 1 ? (
                        <button
                          onClick={() => navigate("/resident/select-property")}
                          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[13px] font-medium ring-1 ring-black/10 bg-white hover:bg-gray-50 transition"
                          title={t("resident.dashboard.switchUnit")}
                        >
                          {unitCode}
                          <ChevronDown className="w-3 h-3 opacity-60" />
                        </button>
                      ) : (
                        <span>{unitCode}</span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <Button
                size="sm"
                onClick={handleLogout}
                title={t("common.logout")}
                // white/black Quiet look (force white bg + black text)
                className="gap-2 rounded-xl bg-white text-gray-900 ring-1 ring-black/5 border-0 hover:bg-white active:bg-gray-50"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t("common.logout")}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 py-4 pb-28 sm:pb-32">
          <Outlet />
        </div>
      </main>

      {/* Floating bottom dock (glass) */}
      <nav className="fixed bottom-3 left-0 right-0 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-w-5xl px-2">
          <ul className="grid grid-cols-6 gap-1 rounded-2xl bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 p-2">
            <Tab to="/resident" icon={<Home className="w-5 h-5" />} label={t("nav.home")} />
            <Tab to="/resident/announcements" icon={<Megaphone className="w-5 h-5" />} label={t("nav.news")} />
            <Tab to="/resident/payments" icon={<CreditCard className="w-5 h-5" />} label={t("nav.payments")} />
            <Tab to="/resident/polling" icon={<BarChart3 className="w-5 h-5" />} label={t("nav.polls")} />
            <Tab to="/resident/issues" icon={<AlertTriangle className="w-5 h-5" />} label={t("nav.issues")} />
            <Tab to="/resident/profile" icon={<User className="w-5 h-5" />} label={t("nav.profile")} />
          </ul>
        </div>
      </nav>
    </div>
  );
}

function Tab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end
        className={({ isActive }: { isActive: boolean }) =>
          [
            "group relative flex flex-col items-center justify-center rounded-xl py-2 transition",
            "focus-visible:ring-2 focus-visible:ring-[color:var(--brand)]/30 outline-none",
          ].join(" ")
        }
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <span
              className="grid place-items-center transition-colors"
              style={{ color: isActive ? PRIMARY : "#4b5563" /* gray-600 */ }}
            >
              {icon}
            </span>
            <span
              className="text-[11px] mt-1 font-medium transition-colors"
              style={{ color: isActive ? PRIMARY : "#4b5563" }}
            >
              {label}
            </span>

            {/* tiny active indicator */}
            <span
              aria-hidden
              className="absolute -bottom-0.5 h-1 w-6 rounded-full transition-opacity"
              style={{
                backgroundColor: PRIMARY,
                opacity: isActive ? 0.7 : 0,
              }}
            />
          </>
        )}
      </NavLink>
    </li>
  );
}

function titleFromPath(path: string, t: (key: string) => string) {
  if (path.includes("/announcements")) return t("resident.title.announcements");
  if (path.includes("/polling")) return t("resident.title.polls");
  if (path.includes("/issues")) return t("resident.title.issues");
  if (path.includes("/payments")) return t("resident.title.payments");
  if (path.includes("/profile")) return t("resident.title.profile");
  return t("resident.title.dashboard");
}
